// connect/api.ts — the two writes a person makes to a connection from the page,
// in one place, so the invite card, the tools list and the connect window all
// say the same thing to /api/connections (app/api/connections/route.ts).
//
//   renameConnection — PATCH {id, label}: a connection that exists (minted by
//                      hand, or adopted by its first call) takes a new name.
//   keepArrival      — POST {tool_id, label, client_id}: a grant that has no
//                      connection behind it yet becomes one, bound to the
//                      client that authorized (the route only honours a
//                      client_id the person holds a live grant for).
//
// Both answer plainly and never throw: an offline press keeps what was there.

import type { Connection } from './connections'

type Fetch = typeof fetch

const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** Rename a connection. true once the server took the new name. */
export async function renameConnection(id: string, label: string, f: Fetch = fetch): Promise<boolean> {
  const next = label.trim()
  if (!id || !next) return false
  try {
    const res = await f('/api/connections', {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, label: next }),
    })
    return res.ok
  } catch {
    return false
  }
}

/** Keep a tool that arrived on its own under the name it was offered (or the
 *  one the person gave it). The new connection, or null when it was not kept. */
export async function keepArrival(
  arrival: { toolId: string; label: string; clientId: string },
  f: Fetch = fetch,
): Promise<Connection | null> {
  const label = arrival.label.trim()
  if (!arrival.toolId || !label || !arrival.clientId) return null
  try {
    const res = await f('/api/connections', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ tool_id: arrival.toolId, label, client_id: arrival.clientId }),
    })
    if (!res.ok) return null
    const data = (await res.json().catch(() => ({}))) as { connection?: Connection }
    return data.connection ?? null
  } catch {
    return null
  }
}
